// Pure, immutable game logic. The board mutates only through applyMove(), which
// is called from main.ts once the drop animation finishes (not in the tap
// handler) — win check and turn switch happen there.
import {
  Cell, EMPTY, HUMAN, BOT, Player, GameState, Stats, COLS, ROWS, idx,
} from './types';

export function emptyBoard(): Cell[] {
  return new Array(COLS * ROWS).fill(EMPTY) as Cell[];
}

export function createState(stats: Stats): GameState {
  return {
    board: emptyBoard(),
    currentPlayer: 'human',
    phase: 'menu',
    moves: 0,
    winner: null,
    winLine: null,
    stats,
  };
}

export const playerCell = (p: Player): Cell => (p === 'human' ? HUMAN : BOT);

/** Lowest empty row in a column, or -1 if the column is full. */
export function dropRow(board: Cell[], col: number): number {
  for (let r = 0; r < ROWS; r++) if (board[idx(col, r)] === EMPTY) return r;
  return -1;
}

const DIRS: ReadonlyArray<[number, number]> = [[1, 0], [0, 1], [1, 1], [1, -1]];

/**
 * Win check anchored at a freshly placed cell. Scans the 4 axes in both
 * directions; returns the full run of board indices if it reaches ≥4, else null.
 */
export function checkWinAt(board: Cell[], col: number, row: number, cell: Cell): number[] | null {
  for (const [dc, dr] of DIRS) {
    const line = [idx(col, row)];
    let c = col + dc, r = row + dr;
    while (c >= 0 && c < COLS && r >= 0 && r < ROWS && board[idx(c, r)] === cell) {
      line.push(idx(c, r)); c += dc; r += dr;
    }
    c = col - dc; r = row - dr;
    while (c >= 0 && c < COLS && r >= 0 && r < ROWS && board[idx(c, r)] === cell) {
      line.unshift(idx(c, r)); c -= dc; r -= dr;
    }
    if (line.length >= 4) return line;
  }
  return null;
}

/**
 * Drop the current player's disc into a column. Returns a new state with the
 * board updated, win/draw resolved, stats bumped and the turn switched. A full
 * column is a no-op (caller guards against it).
 */
export function applyMove(state: GameState, col: number): GameState {
  const row = dropRow(state.board, col);
  if (row < 0) return state;

  const cell = playerCell(state.currentPlayer);
  const board = state.board.slice();
  board[idx(col, row)] = cell;
  const moves = state.moves + 1;

  const winLine = checkWinAt(board, col, row, cell);
  if (winLine) {
    const winner = state.currentPlayer;
    const stats: Stats = { ...state.stats };
    if (winner === 'human') stats.wins++; else stats.losses++;
    return { ...state, board, moves, phase: 'won', winner, winLine, stats };
  }

  if (moves >= COLS * ROWS) {
    const stats: Stats = { ...state.stats, draws: state.stats.draws + 1 };
    return { ...state, board, moves, phase: 'draw', winner: null, winLine: null, stats };
  }

  return {
    ...state, board, moves,
    currentPlayer: state.currentPlayer === 'human' ? 'bot' : 'human',
  };
}
